import { IResource } from '@gettactic/api';
import { IconValue } from '@gettactic/components';
import { LucideIcon } from 'lucide-react';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { Furniture } from '../../../../furniture-list';
import {
  DrawShape,
  DrawToolState,
  GroupShape,
  ResourceEditingOptions,
  Shape,
  SpotShape,
  CircleShape
} from '../../../../types';
import { getRandomColor } from '../../../../utils/colors';
import { generateShapeId } from '../../../../utils/generateShapeId';
import {
  createNodeShape,
  createRectangle,
  createSvgElement,
  createText,
  createCircle,
  createImageElement
} from '../../../../utils/shapeFactory';
import { loadSvgFile } from '../../../../utils/svg';
import {
  calculateGroupBounds,
  flattenGroup
} from '../../../../utils/transform';
import { HistoryManager } from '../../editor/helpers/history';
import { getWindowKonvaObject } from '../../helpers/konva/getWindowKonvaObject';
import {
  MapEditorTool,
  PreviewShape,
  ShapeEditingHighlightOptions
} from '../../types';
import { MapCanvasValue, useMapCanvas } from '../MapCanvasContext';
import {
  AddImageFromFileOptions,
  AddSVGFromFileOptions,
  ClipboardPasteOptions,
  DuplicateShapeOptions,
  MapEditorClipboard,
  MapEditorPasteEvent,
  MapEditorState
} from './MapEditorContext.types';
import { MapEditorContext } from './useMapEditor';

export interface MapEditorProviderProps {
  children: React.ReactNode;
  resources?: IResource[];
  setEditingResources: (value: ResourceEditingOptions) => void;
  editingResources?: ResourceEditingOptions;
  editorRef?: React.RefObject<MapEditorState | null | undefined>;
  onSave?: (data: MapCanvasValue) => void;
}

export const MapEditorProvider: React.FC<MapEditorProviderProps> = ({
  children,
  resources,
  setEditingResources,
  editingResources,
  editorRef,
  onSave
}) => {
  const mapCanvas = useMapCanvas();

  const {
    shapes,
    setShapes: setMapShapes,
    stagePosition,
    setStagePosition,
    gridSize,
    getCanvasValue
  } = mapCanvas;
  const shouldPreventDeselect = React.useRef(false);
  const [selectedIds, _setSelectedIds] = React.useState<string[]>([]);
  const [showOutside, setShowOutside] = React.useState(true);
  const [snapToGrid, setSnapToGrid] = React.useState(false);
  const [showSnapLines, setShowSnapLines] = React.useState(true);
  const [showGrid, setShowGrid] = React.useState(true);
  const [activeTool, setActiveTool] = React.useState<MapEditorTool>('select');
  const [clipboard, setClipboard] = React.useState<Shape[]>([]);
  const [nodeStyleToolTarget, setNodeStyleToolTarget] =
    React.useState<SpotShape | null>(null);

  const [previewShape, setPreviewShape] = React.useState<PreviewShape | null>(
    null
  );

  const [iconToolSelectedIcon, setIconToolSelectedIcon] =
    React.useState<IconValue | null>(null);
  const [furnitureToolSelectedFurniture, setFurnitureToolSelectedFurniture] =
    React.useState<Furniture | null>(null);

  const [contextMenuShape, setContextMenuShape] = React.useState<Shape | null>(
    null
  );
  const [canUndo, setCanUndo] = React.useState(false);
  const [canRedo, setCanRedo] = React.useState(false);

  const [drawTool, setDrawTool] = React.useState<DrawToolState>({
    active: false,
    selectedShape: 'rectangle',
    startPoint: null
  });

  const [highlightedShape, setHighlightedShape] =
    React.useState<ShapeEditingHighlightOptions | null>(null);

  const historyManager = React.useRef(
    new HistoryManager({ shapes: [], selectedIds: [] })
  );

  const updateShape = React.useCallback(
    (id: string, newProps: Partial<Shape>) => {
      const updateInGroup = (shapes: Shape[]): Shape[] => {
        return shapes.map((shape) => {
          if (shape.id === id) {
            return { ...shape, ...newProps } as Shape;
          }

          if (shape.type === 'group' && Array.isArray(shape.children)) {
            return {
              ...shape,
              children: updateInGroup(shape.children)
            };
          }

          return shape;
        });
      };

      setShapes((prev) => updateInGroup(prev));
    },
    []
  );

  const useShapeKeyboardMovement = React.useCallback(() => {
    React.useEffect(() => {
      if (selectedIds.length === 0) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        const activeElement = document.activeElement;

        if (
          activeElement &&
          ['INPUT', 'TEXTAREA'].includes(activeElement.tagName)
        ) {
          return;
        }

        const minPixels = snapToGrid ? gridSize : 1;
        const MOVE_SPEED = e.shiftKey ? minPixels * 5 : minPixels;

        let dx = 0;
        let dy = 0;

        switch (e.key) {
          case 'ArrowUp':
            dy = -MOVE_SPEED;
            break;
          case 'ArrowDown':
            dy = MOVE_SPEED;
            break;
          case 'ArrowLeft':
            dx = -MOVE_SPEED;
            break;
          case 'ArrowRight':
            dx = MOVE_SPEED;
            break;
          default:
            return;
        }

        e.preventDefault();

        const selectedShapes = shapes.filter((shape) =>
          selectedIds.includes(shape.id)
        );
        const updates = selectedShapes.map((shape) => {
          let newX = shape.x + dx;
          let newY = shape.y + dy;

          if (snapToGrid) {
            newX = Math.round(newX / gridSize) * gridSize;
            newY = Math.round(newY / gridSize) * gridSize;
          }

          return {
            id: shape.id,
            x: newX,
            y: newY
          };
        });

        updates.forEach((update) => {
          updateShape(update.id, { x: update.x, y: update.y });
        });
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [selectedIds, shapes, gridSize, snapToGrid, updateShape]);
  }, [selectedIds, shapes, gridSize, snapToGrid, updateShape]);

  useShapeKeyboardMovement();

  const updateHistory = React.useCallback(
    (shapes: Shape[], selectedIds: string[]) => {
      historyManager.current.pushState({ shapes, selectedIds });
      setCanUndo(historyManager.current.canUndo());
      setCanRedo(historyManager.current.canRedo());
    },
    []
  );

  const setSelectedIds = React.useCallback(
    (ids: string[] | ((prev: string[]) => string[])) => {
      if (!editingResources?.isOpen) {
        return _setSelectedIds(ids);
      }

      if (typeof ids === 'function') {
        // Handle function-based updates
        _setSelectedIds((prev) => {
          const newIds = ids(prev); // Compute new value

          const resourceIds = (shapes as SpotShape[])
            .filter(
              (shape) => shape.type === 'spot' && newIds.includes(shape.id)
            )
            .map((shape) => shape.resource_id)
            .filter(Boolean) as string[];

          setEditingResources({ isOpen: true, resourceIds });
          return newIds;
        });
      } else {
        // Handle direct value updates
        const resourceIds = (shapes as SpotShape[])
          .filter((shape) => shape.type === 'spot' && ids.includes(shape.id))
          .map((shape) => shape.resource_id)
          .filter(Boolean) as string[];
        setEditingResources({ isOpen: true, resourceIds });
        _setSelectedIds(ids);
      }
    },
    [shapes, editingResources, setEditingResources]
  );

  const setShapes: (
    action: React.SetStateAction<Shape[]>,
    allowHistoryUpdate?: boolean
  ) => void = React.useCallback(
    (action, allowHistoryUpdate = true) => {
      setMapShapes((prevShapes) => {
        const newShapes =
          typeof action === 'function' ? action(prevShapes) : action;

        if (allowHistoryUpdate) updateHistory(newShapes, selectedIds);

        return newShapes;
      });
    },
    [setMapShapes, updateHistory, selectedIds]
  );

  const undo = React.useCallback(() => {
    const previousState = historyManager.current.undo();
    if (previousState) {
      setShapes(previousState.shapes, false);
      setSelectedIds(previousState.selectedIds);
      setCanUndo(historyManager.current.canUndo());
      setCanRedo(historyManager.current.canRedo());
    }
  }, []);

  const redo = React.useCallback(() => {
    const nextState = historyManager.current.redo();
    if (nextState) {
      setShapes(nextState.shapes, false);
      setSelectedIds(nextState.selectedIds);
      setCanUndo(historyManager.current.canUndo());
      setCanRedo(historyManager.current.canRedo());
    }
  }, []);

  const addShape = React.useCallback(
    (position?: { x?: number; y?: number }) => {
      const newShape = createRectangle(position);
      setShapes((prev) => [...prev, newShape]);
    },
    []
  );

  const disableHistoryRecording = React.useCallback(() => {
    historyManager.current.disableRecording();
  }, []);

  const enableHistoryRecording = React.useCallback(() => {
    historyManager.current.enableRecording();
  }, []);

  const addSvg = React.useCallback((position?: { x?: number; y?: number }) => {
    const newShape = createSvgElement(position);
    setShapes((prev) => [...prev, newShape]);
  }, []);

  const addSvgFromFile = React.useCallback(
    async (file: string, options?: AddSVGFromFileOptions) => {
      const svgShape = options?.shape;
      try {
        const svgData = await loadSvgFile(file);
        const response = await fetch(svgData.blobUrl);
        const svgText = await response.text();
        const newShape = createSvgElement(
          svgShape?.x || svgShape?.y
            ? { x: svgShape?.x, y: svgShape?.y }
            : undefined,
          options?.saveOriginalUrl ? file : svgData.blobUrl,
          options?.saveContent !== false ? svgText : undefined,
          svgData.width,
          svgData.height
        );

        newShape.layerType = svgShape?.layerType || 'shapes';
        console.log('SHAPE TYP$', newShape.layerType);
        if (svgShape?.locked) newShape.locked = svgShape.locked;
        setShapes((prev) => [...prev, newShape]);

        return { shapes: [newShape], svg: { ...svgData } };
      } catch (error) {
        console.error('Error adding SVG from file:', error);
      }

      return undefined;
    },
    []
  );

  const addImageFromFile = React.useCallback(
    async (file: string, options?: AddImageFromFileOptions) => {
      const imageShape = options?.shape;
      try {
        const response = await fetch(file);
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);

        const image = new Image();
        const imageLoadPromise = new Promise<{ width: number; height: number }>(
          (resolve, reject) => {
            image.onload = () =>
              resolve({ width: image.width, height: image.height });
            image.onerror = reject;
          }
        );
        image.src = imageUrl;

        const { width, height } = await imageLoadPromise;

        const newShape = createImageElement(
          imageShape?.x || imageShape?.y
            ? { x: imageShape?.x, y: imageShape?.y }
            : undefined,
          options?.saveOriginalUrl ? file : imageUrl,
          width,
          height
        );
        newShape.layerType = imageShape?.layerType || 'shapes';
        if (imageShape?.locked) newShape.locked = imageShape.locked;
        setShapes((prev) => [...prev, newShape]);

        return { shapes: [newShape], image: { width, height, blobUrl: '' } };
      } catch (error) {
        console.error('Error adding image from file:', error);
      }

      return undefined;
    },
    []
  );

  const addIconSvg = React.useCallback(
    (Icon: LucideIcon, position?: { x?: number; y?: number }) => {
      const svgString = ReactDOMServer.renderToString(<Icon size={24} />);

      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const pathData = URL.createObjectURL(blob);

      const newShape = createSvgElement(position, pathData);
      setShapes((prev) => [...prev, newShape]);
    },
    []
  );

  const addCustomMultiShape = React.useCallback(
    (position?: { x?: number; y?: number }) => {
      const newShape = createNodeShape(position);
      setShapes((prev) => [...prev, newShape]);
    },
    []
  );

  const addText = React.useCallback((position?: { x?: number; y?: number }) => {
    const newShape = createText(position);
    setShapes((prev) => [...prev, newShape]);
  }, []);

  const updateShapes = React.useCallback(
    (ids: string[], propertiesToUpdate: Partial<Shape>) => {
      const updateInGroup = (shapes: Shape[]): Shape[] => {
        return shapes.map((shape) => {
          if (ids.includes(shape.id)) {
            return { ...shape, ...propertiesToUpdate } as Shape;
          }

          if (shape.type === 'group' && Array.isArray(shape.children)) {
            return {
              ...shape,
              children: updateInGroup(shape.children)
            };
          }

          return shape;
        });
      };

      setShapes((prev) => updateInGroup(prev));
    },
    []
  );

  const deleteShape = React.useCallback((id: string | string[]) => {
    const deleteInGroup = (shapes: Shape[]): Shape[] => {
      return shapes
        .filter((shape) =>
          Array.isArray(id) ? !id.includes(shape.id) : shape.id !== id
        )
        .map((shape) =>
          shape.type === 'group' && Array.isArray(shape.children)
            ? { ...shape, children: deleteInGroup(shape.children) }
            : shape
        );
    };

    setShapes((prev) => deleteInGroup(prev));

    setSelectedIds((prev) =>
      prev.filter((selectedId) =>
        Array.isArray(id) ? !id.includes(selectedId) : selectedId !== id
      )
    );
  }, []);

  const moveShape = React.useCallback(
    (id: string, direction: 'up' | 'down') => {
      setShapes((prev) => {
        // Helper function to find and move shape inside a group
        const moveInGroup = (shapes: Shape[]): Shape[] => {
          const index = shapes.findIndex((shape) => shape.id === id);
          if (index === -1) {
            return shapes.map((shape) =>
              shape.type === 'group' && Array.isArray(shape.children)
                ? { ...shape, children: moveInGroup(shape.children) }
                : shape
            );
          }

          const newShapes = [...shapes];
          if (direction === 'up' && index < shapes.length - 1) {
            [newShapes[index], newShapes[index + 1]] = [
              newShapes[index + 1],
              newShapes[index]
            ];
          } else if (direction === 'down' && index > 0) {
            [newShapes[index], newShapes[index - 1]] = [
              newShapes[index - 1],
              newShapes[index]
            ];
          }
          return newShapes;
        };

        return moveInGroup(prev);
      });
    },
    []
  );

  const duplicateShape = React.useCallback(
    (id: string | string[], options?: DuplicateShapeOptions) => {
      const ids = Array.isArray(id) ? id : [id];

      const duplicateRecursive = (shape: Shape): Shape => {
        let override: Partial<Shape> | undefined = undefined;

        if (Array.isArray(options?.override)) {
          override = options.override.find(
            (o) => o.id === shape.id
          ) as Partial<Shape>;
        } else {
          override = options as Partial<Shape>;
        }

        const newShape = {
          ...shape,
          x: shape.x + (options?.offset?.x ?? 20),
          y: shape.y + (options?.offset?.y ?? 20),
          ...override,
          id: generateShapeId(shape.type)
        };

        if (shape.type === 'spot') {
          return {
            ...newShape,
            resource_id:
              options?.switchResourceId && ids.length === 1
                ? shape.resource_id
                : undefined
          } as Shape;
        }

        if (shape.type === 'group' && Array.isArray(shape.children)) {
          return {
            ...newShape,
            children: shape.children.map(duplicateRecursive) // Recursively duplicate children
          } as Shape;
        }

        return newShape as Shape;
      };

      const selectedShapes = shapes.filter((shape) => ids.includes(shape.id));

      if (selectedShapes.length === 0) return;

      const newShapes = selectedShapes.map(duplicateRecursive);

      setShapes((prev) => {
        let updatedShapes = [...prev];

        if (options?.switchResourceId && ids.length === 1) {
          updatedShapes = prev.map((shape) =>
            shape.type === 'spot' && ids.includes(shape.id)
              ? { ...shape, resource_id: undefined }
              : shape
          );
        }

        if (options?.switchIndex && ids.length === 1) {
          const index = prev.findIndex((shape) => shape.id === ids[0]);
          updatedShapes.splice(index, 0, ...newShapes);
          return [...updatedShapes];
        }

        return [...updatedShapes, ...newShapes];
      });

      if (options?.selectDuplicates !== false) {
        setTimeout(() => {
          setSelectedIds(newShapes.map((shape) => shape.id));
        }, 50);
      }

      return newShapes;
    },
    [shapes]
  );

  const toggleOutside = React.useCallback(() => {
    setShowOutside((prev) => !prev);
  }, []);

  const toggleSnapLines = React.useCallback(() => {
    setShowSnapLines((prev) => !prev);
  }, []);

  const toggleGrid = React.useCallback(() => {
    setShowGrid((prev) => !prev);
  }, []);

  const groupSelected = React.useCallback(() => {
    if (selectedIds.length < 2) return;

    const selectedShapes = shapes.filter((shape) =>
      selectedIds.includes(shape.id)
    );

    const flattenedShapes = selectedShapes.flatMap((shape) =>
      shape.type === 'group' ? flattenGroup(shape) : [shape]
    );

    const bounds = calculateGroupBounds(flattenedShapes);

    const groupShape: GroupShape = {
      id: `group-${Date.now()}`,
      type: 'group',
      x: bounds.minX,
      y: bounds.minY,
      width: bounds.maxX - bounds.minX,
      height: bounds.maxY - bounds.minY,
      color: getRandomColor(),
      children: selectedShapes.map((shape) => ({
        ...shape,
        x: shape.x - bounds.minX,
        y: shape.y - bounds.minY
      })),
      layerType: 'shapes'
    };

    setShapes((prev) => [
      ...prev.filter((shape) => !selectedIds.includes(shape.id)),
      groupShape
    ]);
    setSelectedIds([groupShape.id]);
  }, [shapes, selectedIds]);

  const ungroupShapes = React.useCallback(
    (groupId: string) => {
      const group = shapes.find((shape) => shape.id === groupId) as GroupShape;
      if (!group) return;

      const ungroupedShapes = [...group.children];

      // Calculate new positions for ungrouped shapes
      const groupX = group.x;
      const groupY = group.y;
      const newShapes = ungroupedShapes.map((shape) => {
        return {
          ...shape,
          x: shape.x + groupX,
          y: shape.y + groupY
        };
      });

      setShapes((prev) => [
        ...prev.filter((shape) => shape.id !== groupId),
        ...newShapes
      ]);
      setSelectedIds(newShapes.map((shape) => shape.id));

      return newShapes;
    },
    [shapes, selectedIds]
  );

  const copySelectedShapes = React.useCallback(():
    | MapEditorClipboard
    | undefined => {
    if (selectedIds.length === 0) return;
    const selectedShapes = shapes.filter((shape) =>
      selectedIds.includes(shape.id)
    );
    setClipboard(JSON.parse(JSON.stringify(selectedShapes))); // Deep clone to avoid reference issues

    return { shapes: selectedShapes };
  }, [shapes, selectedIds]);

  const pasteShapes = React.useCallback(
    (options?: ClipboardPasteOptions): MapEditorPasteEvent | undefined => {
      if (clipboard.length === 0) return;

      const offset = 20;

      const duplicateShape = (shape: Shape): Shape => {
        const newShape = {
          ...shape,
          id: generateShapeId(shape.type),
          x: shape.x + offset,
          y: shape.y + offset,
          ...options?.shapeOverrides
        } as Shape;

        if (shape.type === 'spot') {
          return { ...newShape, resource_id: undefined } as Shape;
        }

        if (shape.type === 'group' && Array.isArray(shape.children)) {
          return {
            ...newShape,
            children: shape.children.map(duplicateShape) // Recursively duplicate children
          } as Shape;
        }

        return newShape;
      };

      const newShapes = clipboard.map(duplicateShape);

      setShapes((prev) => [...prev, ...newShapes]);
      setSelectedIds(newShapes.map((shape) => shape.id));

      return { newShapes, position: { x: offset, y: offset } };
    },
    [clipboard]
  );

  const locateShape = React.useCallback(
    (id: string) => {
      const shape = shapes.find((s) => s.id === id);
      if (!shape) return;

      const stage = getWindowKonvaObject().stages[0];
      if (!stage) return;

      const stageWidth = stage.width();
      const stageHeight = stage.height();

      const shapeCenterX = shape.x + shape.width / 2;
      const shapeCenterY = shape.y + shape.height / 2;

      const newX = stageWidth / 2 - shapeCenterX * stagePosition.scale;
      const newY = stageHeight / 2 - shapeCenterY * stagePosition.scale;

      setStagePosition((prev) => ({
        ...prev,
        x: newX,
        y: newY
      }));
    },
    [shapes, stagePosition.scale]
  );

  const handleDrawToolUpdate = React.useCallback((shape: DrawShape) => {
    setDrawTool((prev) => ({ ...prev, selectedShape: shape }));
  }, []);

  const handleDrawStart = React.useCallback(
    (point: { x: number; y: number }) => {
      setDrawTool((prev) => ({ ...prev, startPoint: point }));
    },
    []
  );

  const handleDrawEnd = React.useCallback(
    (
      start: { x: number; y: number },
      end: { x: number; y: number },
      shape: DrawShape
    ) => {
      const width = Math.abs(end.x - start.x);
      const height = Math.abs(end.y - start.y);
      const x = Math.min(start.x, end.x);
      const y = Math.min(start.y, end.y);

      let newShape: Shape;
      switch (shape) {
        case 'circle': {
          newShape = createCircle({ x, y }) as CircleShape;
          newShape.width = width;
          newShape.height = height;
          break;
        }
        case 'star':
          //newShape = createStar({ x, y }, width, height);
          break;
        default:
          newShape = createRectangle({ x, y });
          newShape.width = width;
          newShape.height = height;
      }

      setShapes((prev) => [...prev, newShape]);
      setDrawTool((prev) => ({ ...prev, startPoint: null }));
    },
    []
  );

  const copyNodeStyleToNodeTarget = (source: string, target: string) => {
    const sourceShape = shapes.find(
      (shape) => shape.id === source
    ) as SpotShape;
    const targetShape = shapes.find(
      (shape) => shape.id === target
    ) as SpotShape;

    if (!sourceShape || !targetShape) return;

    updateShape(targetShape.id, {
      ...sourceShape,
      id: targetShape.id,
      x: targetShape.x,
      y: targetShape.y,
      textProps: {
        ...sourceShape.textProps,
        text: sourceShape.label
      }
    });
  };

  const save = async () => {
    return await onSave?.(getCanvasValue());
  };

  const value: MapEditorState = {
    shapes,
    selectedIds,
    showOutside,
    snapToGrid,
    showSnapLines,
    showGrid,
    activeTool,
    contextMenuShape,
    addShape,
    addSvg,
    addSvgFromFile,
    addImageFromFile,
    addIconSvg,
    addCustomMultiShape,
    addText,
    updateShape,
    updateShapes,
    deleteShape,
    moveShape,
    setSelectedIds: (selection) => {
      setHighlightedShape(null);
      setSelectedIds(selection);
    },
    toggleOutside,
    setSnapToGrid,
    toggleSnapLines,
    toggleGrid,
    setActiveTool,
    groupSelected,
    setContextMenuShape,
    locateShape,
    duplicateShape,
    copySelectedShapes,
    pasteShapes,
    clipboard: {
      shapes: clipboard
    },
    undo,
    redo,
    canUndo,
    canRedo,
    disableHistoryRecording,
    enableHistoryRecording,
    setHighlightedShape,
    highlightedShape,
    iconToolSelectedIcon,
    setIconToolSelectedIcon,
    furnitureToolSelectedFurniture,
    setFurnitureToolSelectedFurniture,
    drawTool,
    handleDrawStart,
    handleDrawEnd,
    setDrawTool: handleDrawToolUpdate,
    previewShape,
    setPreviewShape,
    shouldPreventDeselect,
    resources,
    setEditingResources,
    editingResources,
    nodeStyleToolTarget,
    setNodeStyleToolTarget,
    copyNodeStyleToNodeTarget,
    ungroupShapes,
    save
  };

  if (typeof editorRef === 'function') {
    (editorRef as (value: unknown) => null)(value);
  }

  return (
    <MapEditorContext.Provider value={value}>
      {children}
    </MapEditorContext.Provider>
  );
};
